import React, { useState, useEffect, useContext, useMemo } from "react";
import { Box, Typography } from "@material-ui/core";
import {
  ContactInfoWorkflow,
  IContactInfo,
  TravelerSelectStep,
  ContactInfoStep,
  Header,
  MobilePopoverCard,
  BackButton,
  MobileFloatingButton,
  LoadingPopup,
  B2BSpinner,
  NotificationBanner,
  BannerSeverity,
  Icon,
  IconName,
  emailRegex,
  phoneRegex,
  InformationalModal as TreesModal,
  TravelWalletSingleSelectionStep,
  EarnOfferBanner,
  getHSPEarnOfferDescription,
  TripProtectionBanner,
  useTripProtectionSurvey,
} from "halifax";
import {
  ADD_CONTACT_INFO,
  CarsBookErrorEnum,
  INSURANCE_OFFER_SHOWN,
  INSURANCE_OPT_IN_CLICKED,
  INSURANCE_SURVEY_RESPONSE_SUBMITTED,
  ITravelerStepErrors,
  PaymentError,
  UserPreferenceKeyEnum,
} from "redmond";
import { RouteComponentProps } from "react-router";
import "./styles.scss";
import clsx from "clsx";
import { ProductError } from "@b2bportal/purchase-api";
import {
  CDN_ASSETS,
  getConfigTenant,
  isVesperTenant,
  useShowPolicyBanner,
} from "@capone/common";
import dayjs from "dayjs";
import { MobileCarBookWorkflowConnectorProps } from "./container";
import {
  PriceBreakdown,
  CarBookPassengerSelection,
  PaymentCard,
  CarBookMobileButton,
  CarBookSummaryPanel,
  PriceBreakdownDropdown,
  AGENT_FEE,
  TRIP_PROTECTION_COVERAGE_OPTIONS,
} from "../../index";
import {
  CONTACT_INFO_SAVE,
  CONTINUE,
  CONTACT_INFO_TITLE,
  CONTACT_INFO_SUBTITLE,
  REVIEW_MY_TRIP_TEXT,
  PRICE_QUOTE_MESSAGE,
  TITLE,
  SUBTITLE,
  UNDER_25_WARNING,
  TREES_MODAL_CTA_TEXT,
  TREES_MODAL_HEADER,
  TREES_MODAL_SUBTITLE,
  TREES_MODAL_TITLE,
  TREES_BOLDED_MODAL_CTA_TEXT,
  UPDATE_DRIVER,
} from "./textConstants";
import { PATH_BOOK_CONFIRMATION } from "../../../../../utils/paths";
import { ClientContext } from "../../../../../App";
import { CARS_REWARDS_DISABLED_BANNER_TEXT } from "../../../../rewards/components/RewardsBanner/constants";
import { CarShopSmallMap } from "../../../../shop/components/CarShopSmallMap/component";
import { trackEvent } from "../../../../../api/v1/analytics/trackEvent";
import { fetchUserMetadata } from "../../../../../api/v1/user/fetchUserMetadata";
import { updateUserMetadata } from "../../../../../api/v1/user/updateUserMetadata";
import { fetchCustomerDetails } from "../../../../../api/v1/customer/fetchCustomerDetails";
import {
  AVAILABLE,
  CARS_CX_V1,
  CARS_TRAVEL_INSURANCE_SMOKE_TEST,
  CREDIT_OFFER_STACKING_V1,
  getExperimentVariant,
  TRAVEL_CREDIT_HISTORY_EXPERIMENT,
  TRAVEL_WALLET_CREDITS_EXPERIMENT,
  TRAVEL_WALLET_OFFER_EXPERIMENT,
  TREES_MODAL_EXPERIMENT,
  useExperiments,
  XMOD_V1_EXPERIMENT,
  CARS_FLIGHT_NUMBER_INPUT,
} from "../../../../../context/experiments";
import {
  HertzLoyaltyStep,
  HertzLoyaltyWorkflow,
} from "../../MobileHertzLoyaltyWorkflow";
import { TravelOfferSelection } from "../../TravelOfferSelection";
import { parseQueryString } from "../../../utils/parseQueryString";

export interface IMobileCarBookWorkflowProps
  extends RouteComponentProps,
    MobileCarBookWorkflowConnectorProps {
  checkoutStep: MobileCarBookWorkflowStep;
  setCheckoutStep: React.Dispatch<
    React.SetStateAction<MobileCarBookWorkflowStep>
  >;
}

export enum MobileCarBookWorkflowStep {
  TravelerInformation,
  ContactInformation,
  HertzRewards,
  TravelOfferSelection,
  RewardsAndPayment,
  Review,
}

export const MobileCarBookWorkflow = ({
  priceQuoteInProgress,
  hasNoUserPassengers,
  schedulePriceQuote,
  history,
  reservation,
  isBookingValid,
  setContactInfo,
  confirmationEmail,
  confirmationPhoneNumber,
  priceDifferenceAcknowledged,
  scheduleBook,
  selectedVehicle,
  driverAge,
  canBook,
  isHertzProvider,
  priceQuoteErrors,
  confirmationDetailsErrors,
  hertzLoyaltyRewardsNumber,
  offers,
  fetchApplicableTravelWalletItems,
  errorTitles,
  resetPaymentCardSelectedAccounts,
  setPriceQuote,
  priceQuote,
  credit,
  isTravelWalletPaymentOnly,
  creditToApply,
  fetchTravelWalletCreditHistory,
  useV1PurchaseFlow,
  isTravelApp,
  checkoutStep,
  setCheckoutStep,
  offerToApply,
  insuranceBaseTrackingProperties,
  hasFlightInfoField,
  isFlightInfoValid,
}: IMobileCarBookWorkflowProps) => {
  const clientContext = useContext(ClientContext);
  const isVesper = isVesperTenant(getConfigTenant());
  const { sessionInfo, isAgentPortal, policies, isRewardsDisabled } =
    clientContext;
  const expState = useExperiments();

  const carsTravelInsuranceSmokeTestExperiment = getExperimentVariant(
    expState.experiments,
    CARS_TRAVEL_INSURANCE_SMOKE_TEST,
  );
  const isCarsTravelInsuranceSmokeTestExperiment = useMemo(
    () => carsTravelInsuranceSmokeTestExperiment === AVAILABLE,
    [carsTravelInsuranceSmokeTestExperiment],
  );

  const {
    showBanner: showTripProtectionBanner,
    onSubmit: onTripProtectionSubmit,
  } = useTripProtectionSurvey({
    enabled: isCarsTravelInsuranceSmokeTestExperiment,
    metadataKey: UserPreferenceKeyEnum.hasSubmittedCarsTripProtectionSurvey,
    fetchUserMetadata,
    updateUserMetadata,
  });
  // TODO: remove this when we decide if using text message - https://hopchat.slack.com/archives/C01FX0M22MV/p1620146159000600
  const [contactInfo, setContact] = useState<IContactInfo | null>({
    phoneNumber: confirmationPhoneNumber || "",
    email: confirmationEmail || sessionInfo?.userInfo?.email || "",
  });
  const [travelerWorkflowStep, setTravelerWorkflowStep] =
    useState<TravelerSelectStep>(TravelerSelectStep.Main);
  const [contactInfoStep, setContactInfoStep] = useState<ContactInfoStep>(
    ContactInfoStep.Main,
  );
  const [customerDetailsLoading, setCustomerDetailsLoading] =
    React.useState<boolean>(true);
  const [openPaymentCard, setOpenPaymentCard] = useState<boolean>(false);
  const [treeModalOpen, setTreeModalOpen] = useState(false);
  const [hertzLoyaltyStep, setHertzLoyaltyStep] = useState<HertzLoyaltyStep>(
    HertzLoyaltyStep.Main,
  );
  const [travelWalletSelectStep, setTravelWalletSelectStep] =
    useState<TravelWalletSingleSelectionStep>(
      TravelWalletSingleSelectionStep.Main,
    );
  const [showErrors, setShowErrors] = useState<ITravelerStepErrors>({
    loyaltyNumber: false,
  });
  const [updatedInfoOnReview, setUpdatedInfoOnReview] = useState(false);

  const isFlightNumberInputEnabled =
    getExperimentVariant(expState.experiments, CARS_FLIGHT_NUMBER_INPUT) ===
    AVAILABLE;
  const showFlightNumberForm = isFlightNumberInputEnabled && hasFlightInfoField;

  const treesModalExperiment = getExperimentVariant(
    expState.experiments,
    TREES_MODAL_EXPERIMENT,
  );
  const isTreesModalExperiment = useMemo(
    () => treesModalExperiment === AVAILABLE,
    [treesModalExperiment],
  );

  const travelWalletOffer = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_OFFER_EXPERIMENT,
  );
  const isTravelWalletOfferExperiment = React.useMemo(
    () => travelWalletOffer === AVAILABLE,
    [travelWalletOffer],
  );

  const travelWalletCreditsExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_CREDITS_EXPERIMENT,
  );
  const isTravelWalletCreditsExperiment = React.useMemo(
    () => travelWalletCreditsExperiment === AVAILABLE,
    [travelWalletCreditsExperiment],
  );

  const creditAndOfferStackingExperimentV1 = getExperimentVariant(
    expState.experiments,
    CREDIT_OFFER_STACKING_V1,
  );
  const isCreditAndOfferStackingExperimentV1 = useMemo(
    () => creditAndOfferStackingExperimentV1 === AVAILABLE,
    [creditAndOfferStackingExperimentV1],
  );

  const travelCreditHistoryExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_CREDIT_HISTORY_EXPERIMENT,
  );
  const isTravelCreditHistoryExperiment = useMemo(
    () => travelCreditHistoryExperiment === AVAILABLE,
    [travelCreditHistoryExperiment],
  );

  const carsCXV1Experiment = getExperimentVariant(
    expState.experiments,
    CARS_CX_V1,
  );
  const isCarsCXV1Experiment = React.useMemo(
    () => carsCXV1Experiment === AVAILABLE,
    [carsCXV1Experiment],
  );

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const onBookCar = () => {
    priceDifferenceAcknowledged || canBook
      ? scheduleBook({
          agentFee: isAgentPortal ? AGENT_FEE : 0,
          initialTime: dayjs().toISOString(),
        })
      : schedulePriceQuote(history, isAgentPortal ? AGENT_FEE : 0);
  };

  const openCarBookPassengerSelection = () =>
    setTravelerWorkflowStep(TravelerSelectStep.TravelerSelect);

  const openContactInfoWorkflow = () => {
    setContactInfoStep(ContactInfoStep.ContactInfoForm);
  };

  const openPaymentSelection = () => setOpenPaymentCard(true);

  const openHertzRewards = () =>
    setHertzLoyaltyStep(HertzLoyaltyStep.HertzLoyaltyForm);

  const openTravelWalletSelection = () => {
    setTravelWalletSelectStep(
      TravelWalletSingleSelectionStep.TravelWalletSelection,
    );
  };
  const incrementCheckoutStep = () => {
    setCheckoutStep((step) => {
      const searchParams = new URLSearchParams(history.location.search);
      if (step === MobileCarBookWorkflowStep.ContactInformation) {
        if (!isHertzProvider && !showFlightNumberForm) {
          if (
            isCreditAndOfferStackingExperimentV1 ||
            ((isTravelWalletCreditsExperiment ? !credit : true) &&
              (isTravelWalletOfferExperiment ? !offers?.length : true))
          ) {
            if (isTravelApp) {
              searchParams.set("bookStep", (step + 3).toString());

              history.push({
                ...history.location,
                search: searchParams.toString(),
              });
            }
            return step + 3;
          }
          if (isTravelApp) {
            searchParams.set("bookStep", (step + 2).toString());

            history.push({
              ...history.location,
              search: searchParams.toString(),
            });
          }
          return step + 2;
        }
      }
      if (step === MobileCarBookWorkflowStep.HertzRewards) {
        if (
          isCreditAndOfferStackingExperimentV1 ||
          ((isTravelWalletCreditsExperiment ? !credit : true) &&
            (isTravelWalletOfferExperiment ? !offers?.length : true))
        ) {
          if (isTravelApp) {
            searchParams.set("bookStep", (step + 2).toString());

            history.push({
              ...history.location,
              search: searchParams.toString(),
            });
          }
          return step + 2;
        }
      }
      if (step === MobileCarBookWorkflowStep.TravelOfferSelection) {
        if (isTravelWalletPaymentOnly) {
          if (isTravelApp) {
            searchParams.set("bookStep", (step + 2).toString());

            history.push({
              ...history.location,
              search: searchParams.toString(),
            });
          }
          return step + 2;
        }
      }
      if (isTravelApp) {
        searchParams.set(
          "bookStep",
          (step === MobileCarBookWorkflowStep.Review
            ? step
            : step + 1
          ).toString(),
        );

        history.push({
          ...history.location,
          search: searchParams.toString(),
        });
      }
      return step === MobileCarBookWorkflowStep.Review ? step : step + 1;
    });
  };

  const parsedQueryString = useMemo(
    () => parseQueryString(history),
    [history.location.search],
  );
  useEffect(() => {
    if (isTravelApp) {
      if (!parsedQueryString?.bookStep) {
        const searchParams = new URLSearchParams(history.location.search);
        searchParams.set("bookStep", checkoutStep.toString());

        history.replace({
          ...history.location,
          search: searchParams.toString(),
        });
      } else if (
        parsedQueryString?.bookStep &&
        Number(parsedQueryString?.bookStep) !== checkoutStep
      ) {
        setCheckoutStep(Number(parsedQueryString?.bookStep));
      }
    }
  }, [checkoutStep, parsedQueryString?.bookStep, isTravelApp]);

  const handleGoBack = () => {
    switch (checkoutStep) {
      case MobileCarBookWorkflowStep.TravelerInformation:
        history.goBack();
        break;
      case MobileCarBookWorkflowStep.RewardsAndPayment:
        if (
          (isTravelWalletCreditsExperiment &&
          !isCreditAndOfferStackingExperimentV1
            ? !credit
            : true) &&
          (isTravelWalletOfferExperiment &&
          !isCreditAndOfferStackingExperimentV1
            ? !offers?.length
            : true)
        ) {
          if (!isHertzProvider && !showFlightNumberForm) {
            setCheckoutStep((step) => step - 3);
          } else {
            setCheckoutStep((step) => step - 2);
          }
        } else {
          setCheckoutStep((step) => step - 1);
        }
        break;
      case MobileCarBookWorkflowStep.TravelOfferSelection:
        if (!isHertzProvider && !showFlightNumberForm) {
          setCheckoutStep((step) => step - 2);
        } else {
          setCheckoutStep((step) => step - 1);
        }
        break;
      default:
        setCheckoutStep((step) => step - 1);
        break;
    }
  };

  const closeAllPopovers = () => {
    setTravelerWorkflowStep(TravelerSelectStep.Main);
    setContactInfoStep(ContactInfoStep.Main);
    setHertzLoyaltyStep(HertzLoyaltyStep.Main);
    setTravelWalletSelectStep(TravelWalletSingleSelectionStep.Main);
    setOpenPaymentCard(false);
  };

  const startSchedulePriceQuoteAndResetPayment = () => {
    if (isHertzProvider || showFlightNumberForm) {
      setCheckoutStep(MobileCarBookWorkflowStep.HertzRewards);
    } else {
      schedulePriceQuote(history, isAgentPortal ? AGENT_FEE : 0, true);
      resetPaymentCardSelectedAccounts();
      setUpdatedInfoOnReview(true);
    }
  };

  React.useEffect(() => {
    if (
      [...priceQuoteErrors, ...confirmationDetailsErrors].some((error) =>
        useV1PurchaseFlow
          ? (error as ProductError)?.value?.value?.msg ===
            CarsBookErrorEnum.InvalidLoyaltyNumber
          : (error as PaymentError)?.msg ===
            CarsBookErrorEnum.InvalidLoyaltyNumber,
      )
    ) {
      setCheckoutStep(MobileCarBookWorkflowStep.HertzRewards);
    }

    if (
      [...priceQuoteErrors, ...confirmationDetailsErrors].some((error) =>
        useV1PurchaseFlow
          ? (error as ProductError)?.value?.value?.msg ===
            CarsBookErrorEnum.LoyaltyNameMismatch
          : (error as PaymentError)?.msg ===
            CarsBookErrorEnum.LoyaltyNameMismatch,
      ) ||
      errorTitles.primaryButtonText === UPDATE_DRIVER
    ) {
      setCheckoutStep(MobileCarBookWorkflowStep.TravelerInformation);
    }
  }, [priceQuoteErrors, confirmationDetailsErrors]);

  useEffect(() => {
    switch (checkoutStep) {
      case MobileCarBookWorkflowStep.TravelerInformation:
        closeAllPopovers();
        openCarBookPassengerSelection();
        break;
      case MobileCarBookWorkflowStep.ContactInformation:
        closeAllPopovers();
        openContactInfoWorkflow();
        break;
      case MobileCarBookWorkflowStep.HertzRewards:
        closeAllPopovers();
        openHertzRewards();
        break;
      case MobileCarBookWorkflowStep.TravelOfferSelection:
        closeAllPopovers();
        openTravelWalletSelection();
        break;
      case MobileCarBookWorkflowStep.RewardsAndPayment:
        closeAllPopovers();
        openPaymentSelection();
        break;
      default:
        break;
    }
  }, [checkoutStep]);

  // note: it handles the PassengerSelection step logic separately from the previous useEffect;
  // when travelerWorkflowStep is being changed too rapidly (e.g.: changing from Main -> TravelerSelect -> TravelerInfoForm because of hasUserPassengers value)
  // it seems to cause some unexpected behaviours on TravelerSelectWorkflow, which in turn causes the Review screen to be unscrollable
  // https://hopper-jira.atlassian.net/browse/BP-1090
  useEffect(() => {
    if (
      checkoutStep === MobileCarBookWorkflowStep.TravelerInformation &&
      hasNoUserPassengers
    ) {
      setTravelerWorkflowStep(TravelerSelectStep.TravelerInfoForm);
    }
  }, [checkoutStep, hasNoUserPassengers]);

  useEffect(() => {
    if (reservation) {
      const params = isTravelApp ? "?isTravelApp=true" : "";
      history.push(`${PATH_BOOK_CONFIRMATION}${params}`);
    }
  }, [reservation]);

  React.useEffect(() => {
    const getCustomerDetails = async () => {
      try {
        const details = await fetchCustomerDetails();
        setContact({
          email: "",
          ...contactInfo,
          phoneNumber: details?.phoneNumber || "",
        });
        setContactInfo(contactInfo?.email || "", details?.phoneNumber || "");
      } finally {
        setCustomerDetailsLoading(false);
      }
    };

    getCustomerDetails();
    isTravelWalletOfferExperiment && fetchApplicableTravelWalletItems();
    isTravelCreditHistoryExperiment && fetchTravelWalletCreditHistory();
  }, []);

  useEffect(() => {
    if (
      checkoutStep === MobileCarBookWorkflowStep.Review &&
      !priceQuoteInProgress &&
      !!priceQuote &&
      updatedInfoOnReview
    ) {
      if (creditToApply) {
        setCheckoutStep(MobileCarBookWorkflowStep.TravelOfferSelection); // if user has selected a travel wallet item before PQ is run again on pax/contact info update, bring them back to that step
      } else {
        handleGoBack();
      }
      setUpdatedInfoOnReview(false);
    }
  }, [checkoutStep, priceQuoteInProgress, priceQuote, updatedInfoOnReview]);

  useEffect(() => {
    if (priceQuoteInProgress && !!priceQuote) {
      setPriceQuote(null);
    }
  }, [priceQuoteInProgress]);

  useEffect(() => {
    setTimeout(() => window.scrollTo(0, 0), 100);
  }, [checkoutStep]);

  const isUnderAge = (driverAge || 0) < 25;

  const showPolicyBanner = useShowPolicyBanner(policies);

  const renderBackButton = () => (
    <BackButton
      className="mobile-car-book-header-go-back"
      onClick={handleGoBack}
    />
  );

  return (
    <>
      <Box
        className={clsx("mobile-car-book-workflow-root", {
          redesigned: isVesper,
          "mobile-review-car-book":
            checkoutStep === MobileCarBookWorkflowStep.Review,
          xmod: isXmodRedesign,
          "travel-app": isTravelApp,
        })}
      >
        {/* TODO: Header should have pricing details */}
        <Header
          className="mobile-car-book-header checkout"
          left={!isTravelApp ? renderBackButton() : undefined}
          right={
            <PriceBreakdownDropdown
              popoverClassName={
                checkoutStep === MobileCarBookWorkflowStep.Review
                  ? "mobile-review-car-book-price-breakdown"
                  : undefined
              }
            />
          }
          isMobile
          fullWidth
        />
        <Box className="title-summary-container">
          <Typography variant="h2" className="title">
            {TITLE}
          </Typography>
          <Typography variant="body1" className="subtitle">
            {SUBTITLE}
          </Typography>
        </Box>
        <CarBookSummaryPanel
          isMobile
          hidePriceAndReward
          isCarsCXV1Experiment={isCarsCXV1Experiment}
          showPolicyBanner={showPolicyBanner}
        />
        <Box className="car-shop-map-container">
          {selectedVehicle && (
            <CarShopSmallMap selectedVehicle={selectedVehicle} isMobile />
          )}
        </Box>
        <CarBookPassengerSelection
          progress={travelerWorkflowStep}
          setProgress={setTravelerWorkflowStep}
          onGoBack={(travelersChanged) => {
            switch (checkoutStep) {
              case MobileCarBookWorkflowStep.TravelerInformation:
                handleGoBack();
                break;
              case MobileCarBookWorkflowStep.Review:
                // note: when the user changes passengers on the final step and then clicks the go-back button,
                // handle it as if the user clicked continue;
                if (travelersChanged) {
                  startSchedulePriceQuoteAndResetPayment();
                }
                break;
              default:
                break;
            }
          }}
          onContinue={(travelersChanged) => {
            switch (checkoutStep) {
              case MobileCarBookWorkflowStep.Review:
                if (travelersChanged) {
                  startSchedulePriceQuoteAndResetPayment();
                }
                break;
              default:
                incrementCheckoutStep();
                break;
            }
          }}
          selectionScreenHeaderElement={<PriceBreakdownDropdown />}
          className="b2b"
          onTravelersStep={
            checkoutStep === MobileCarBookWorkflowStep.TravelerInformation
          }
          isMobile
          useLocalId
          isXmodRedesign={isXmodRedesign}
        />
        <ContactInfoWorkflow
          titles={{
            contactInfoTitle: CONTACT_INFO_TITLE,
            contactInfoSubtitle: CONTACT_INFO_SUBTITLE,
            buttonTitle:
              checkoutStep === MobileCarBookWorkflowStep.Review
                ? CONTACT_INFO_SAVE
                : CONTINUE,
          }}
          progressStep={contactInfoStep}
          setProgressStep={setContactInfoStep}
          isMobile
          contactInfo={contactInfo}
          hideBackButton={isTravelApp}
          onGoBack={() => {
            if (checkoutStep === MobileCarBookWorkflowStep.ContactInformation) {
              handleGoBack();
            }
          }}
          onContactInfoChange={(contactInfo) => {
            setContact(contactInfo);
            setContactInfo(contactInfo.email, contactInfo.phoneNumber);
            if (
              contactInfo.email &&
              contactInfo.phoneNumber &&
              emailRegex.test(contactInfo.email) &&
              phoneRegex.test(contactInfo.phoneNumber)
            ) {
              trackEvent({
                eventName: ADD_CONTACT_INFO,
                properties: {},
              });
            }
            incrementCheckoutStep();
          }}
          mobileHeaderElement={<PriceBreakdownDropdown />}
          className="b2b"
          onContinueClick={() => {
            switch (checkoutStep) {
              case MobileCarBookWorkflowStep.Review:
                startSchedulePriceQuoteAndResetPayment();
                break;
              default:
                isHertzProvider || showFlightNumberForm
                  ? setHertzLoyaltyStep(HertzLoyaltyStep.HertzLoyaltyForm)
                  : schedulePriceQuote(
                      history,
                      isAgentPortal ? AGENT_FEE : 0,
                      true,
                    );
                break;
            }
          }}
          loading={customerDetailsLoading}
          useXmodHeadings={isXmodRedesign}
        />
        {(isHertzProvider || showFlightNumberForm) && (
          <HertzLoyaltyWorkflow
            progressStep={hertzLoyaltyStep}
            setProgressStep={setHertzLoyaltyStep}
            onGoBack={() => {
              if (checkoutStep === MobileCarBookWorkflowStep.HertzRewards) {
                handleGoBack();
              }
            }}
            onContinueClick={() => {
              schedulePriceQuote(history, isAgentPortal ? AGENT_FEE : 0, true);
              incrementCheckoutStep();
            }}
            mobileHeaderElement={<PriceBreakdownDropdown />}
            showErrors={showErrors}
            setShowErrors={setShowErrors}
            hertzLoyaltyRewardsNumber={hertzLoyaltyRewardsNumber}
            showHertzLoyalty={isHertzProvider}
            showFlightNumberForm={showFlightNumberForm}
            isFlightInfoValid={isFlightInfoValid}
          />
        )}
        {isTravelWalletOfferExperiment &&
          !isCreditAndOfferStackingExperimentV1 && (
            <TravelOfferSelection
              progressStep={travelWalletSelectStep}
              mobileHeaderElement={<PriceBreakdownDropdown />}
              isMobile
              onGoBack={() => {
                if (
                  checkoutStep ===
                  MobileCarBookWorkflowStep.TravelOfferSelection
                ) {
                  handleGoBack();
                }
              }}
              onContinueClick={() => {
                incrementCheckoutStep();
                isTravelWalletPaymentOnly &&
                  setTravelWalletSelectStep(
                    TravelWalletSingleSelectionStep.Main,
                  );
              }}
            />
          )}
        <PriceBreakdown />
        {getHSPEarnOfferDescription(offerToApply) && (
          <EarnOfferBanner isMobile feeContext="pick-up" />
        )}
        <Box
          className={clsx("underage-banner-container", {
            "less-padding": isTreesModalExperiment,
          })}
        >
          {isUnderAge && (
            <NotificationBanner
              className="underage-banner"
              label={UNDER_25_WARNING}
              severity={BannerSeverity.INFO}
              icon={
                <Icon className="banner-icon" name={IconName.InfoCircleFill} />
              }
            />
          )}
        </Box>
        {isRewardsDisabled && (
          <NotificationBanner
            className="rewards-disabled-banner"
            label={CARS_REWARDS_DISABLED_BANNER_TEXT}
            severity={BannerSeverity.WARNING}
            icon={<Icon className="banner-icon" name={IconName.WarningAlert} />}
          />
        )}
        {isTreesModalExperiment && (
          <TreesModal
            image={CDN_ASSETS.capone.trees.confirmation}
            header={TREES_MODAL_HEADER}
            title={TREES_MODAL_TITLE}
            onClose={() => setTreeModalOpen(false)}
            subtitle={TREES_MODAL_SUBTITLE}
            icon={<Icon className="trees-icon" name={IconName.TreesIcon} />}
            openModal={treeModalOpen}
            setOpenModal={setTreeModalOpen}
            modalLinkCopy={TREES_MODAL_CTA_TEXT}
            modalButtonCopyStartIcon={
              <Icon className="trees-icon" name={IconName.TreesIcon} />
            }
            isMobile
            boldedModalLinkCopy={TREES_BOLDED_MODAL_CTA_TEXT}
          />
        )}

        <CarBookMobileButton
          hasContactInfo={!!contactInfo}
          openContactInfo={() => {
            setContactInfoStep(ContactInfoStep.ContactInfoForm);
          }}
          isBookingValid={
            isBookingValid && (showFlightNumberForm ? isFlightInfoValid : true)
          }
          onConfirmAndBook={() => onBookCar()}
          // TODO: simplify/trim CarBookMobileButton logic
          showPaymentStep
          onApplyRewards={() => {}}
          paymentCardOpen={openPaymentCard}
        />
      </Box>
      <MobilePaymentCardPopover
        openPaymentCard={openPaymentCard}
        disabled={!isBookingValid}
        onClose={() => setOpenPaymentCard(false)}
        onConfirm={() => {
          incrementCheckoutStep();
          setOpenPaymentCard(false);
        }}
        onGoBack={() => {
          if (checkoutStep === MobileCarBookWorkflowStep.RewardsAndPayment) {
            handleGoBack();
          }
        }}
        mobileHeaderElement={<PriceBreakdownDropdown />}
        isTravelApp={isTravelApp}
        tripProtectionBanner={
          isCarsTravelInsuranceSmokeTestExperiment ? (
            <TripProtectionBanner
              hidden={!showTripProtectionBanner}
              isMobile
              coverageOptions={TRIP_PROTECTION_COVERAGE_OPTIONS}
              onImpression={() =>
                trackEvent({
                  eventName: INSURANCE_OFFER_SHOWN,
                  properties: insuranceBaseTrackingProperties,
                })
              }
              onCtaClick={() =>
                trackEvent({
                  eventName: INSURANCE_OPT_IN_CLICKED,
                  properties: insuranceBaseTrackingProperties,
                })
              }
              onSubmit={(response) => {
                onTripProtectionSubmit();
                trackEvent({
                  eventName: INSURANCE_SURVEY_RESPONSE_SUBMITTED,
                  properties: {
                    ...insuranceBaseTrackingProperties,
                    coverage_options_selected: response.selectedCoverageOptions,
                    likelihood_to_purchase: response.likelihoodToPurchase,
                  },
                });
              }}
            />
          ) : undefined
        }
      />
      <LoadingPopup
        classes={["mobile-price-quote-loading-popup"]}
        open={priceQuoteInProgress}
        message={PRICE_QUOTE_MESSAGE}
        indicator={B2BSpinner}
        indicatorSize="small"
        verticalAlignment="center"
        fullScreen
      />
    </>
  );
};

interface IMobilePaymentCardPopoverProps {
  openPaymentCard: boolean;
  disabled?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onGoBack: () => void;
  mobileHeaderElement?: JSX.Element;
  isTravelApp?: boolean;
  tripProtectionBanner?: JSX.Element;
}

const MobilePaymentCardPopover = (props: IMobilePaymentCardPopoverProps) => {
  const {
    openPaymentCard,
    disabled = true,
    onClose,
    onConfirm,
    onGoBack,
    mobileHeaderElement,
    isTravelApp,
    tripProtectionBanner,
  } = props;

  const renderBackButton = () => (
    <BackButton
      className="car-payment-card-popup-back-button"
      onClick={onGoBack}
    />
  );

  return (
    <MobilePopoverCard
      open={openPaymentCard}
      className={clsx("car-payment-card-popup", "b2b")}
      fullScreen
      onClose={onClose}
      topLeftButton={!isTravelApp ? renderBackButton() : undefined}
      headerElement={mobileHeaderElement}
    >
      <PaymentCard tripProtectionBanner={tripProtectionBanner} />
      {!disabled && (
        <MobileFloatingButton
          className={clsx("car-book-review-trip-button", "b2b")}
          onClick={onConfirm}
          wrapperClassName="b2b"
        >
          {REVIEW_MY_TRIP_TEXT}
        </MobileFloatingButton>
      )}
    </MobilePopoverCard>
  );
};
